import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const Layout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Estados para notificaciones
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Función para cargar notificaciones del día
  const fetchTodayNotifications = async () => {
    setLoadingNotifications(false);
  };

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    fetchTodayNotifications();
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  // Función para manejar clic en notificación
  const handleNotificationClick = async (notification) => {
    try {
      // Buscar la oportunidad relacionada con el AccountId
      const response = await fetch(`${getApiUrl('OPORTUNIDADES', 'BASE')}/account/${notification.AccountId}`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.length > 0) {
          // Tomar la primera oportunidad encontrada
          const opportunity = result.data[0];
          
          // Cerrar el dropdown de notificaciones
          setShowNotifications(false);
          
          // Navegar al dashboard ya que las oportunidades fueron eliminadas
          navigate(`/dashboard`);
          
        } else {
          console.log('No se encontró oportunidad para esta cuenta');
          // Cerrar el dropdown de notificaciones
          setShowNotifications(false);
        }
      } else {
        console.error('Error al buscar oportunidad');
        setShowNotifications(false);
      }
    } catch (error) {
      console.error('Error al manejar clic en notificación:', error);
      setShowNotifications(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      
      {/* Main Content */}
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen 
          ? 'ml-56 xl:ml-64' 
          : 'ml-0 lg:ml-56 xl:ml-64'
      }`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-3 sm:px-4 md:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Botón de menú hamburguesa - Solo visible en móvil */}
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500"
                aria-label="Abrir menú"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs sm:text-sm font-semibold">
                    {user?.firstName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-900">
                    Bienvenido, {user?.firstName && user?.lastName 
                      ? `${user.firstName} ${user.lastName}` 
                      : user?.username}
                  </p>
                  <p className="text-xs text-gray-500">Bitácora Electrónica</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-xs sm:text-sm text-gray-500 hidden md:block">
                {new Date().toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <div className="text-xs text-gray-500 hidden sm:block md:hidden">
                {new Date().toLocaleDateString('es-ES', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short'
                })}
              </div>
              <div className="text-xs text-gray-500 sm:hidden">
                {new Date().toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'short'
                })}
              </div>
              
              {/* Campanita de notificaciones */}
              <div className="relative">
                <button
                  onClick={toggleNotifications}
                  className="relative p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 rounded-full"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9zM13 17a1 1 0 11-2 0 1 1 0 012 0z" />
                  </svg>
                  
                  {/* Indicador de notificaciones */}
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>
                
                {/* Dropdown de notificaciones */}
                {showNotifications && (
                  <div className="notification-dropdown absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900">
                          Notificaciones de Hoy
                        </h3>
                      </div>
                      
                      {loadingNotifications ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          <span className="ml-2">Cargando...</span>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="px-4 py-3 text-center text-gray-500">
                          No hay tareas programadas para hoy
                        </div>
                      ) : (
                        <div className="max-h-64 overflow-y-auto">
                          {notifications.map((notification) => {
                            // Formatear fecha y hora
                            const activityDate = new Date(notification.ActivityDate);
                            const formattedDate = activityDate.toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            });
                            const formattedTime = activityDate.toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                            
                            return (
                              <div 
                                key={notification.Id} 
                                className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex items-start">
                                  <div className="flex-shrink-0">
                                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                                  </div>
                                  <div className="ml-3 flex-1">
                                    <p className="text-sm text-gray-900">
                                      Hoy se programó <span className="font-medium">{notification.Subject}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      a las <span className="font-medium">{formattedTime}</span> para la cuenta <span className="font-medium">{notification.AccountName}</span>
                                    </p>
                                    {notification.Description && (
                                      <p className="text-xs text-gray-400 mt-1">
                                        {notification.Description}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 